import React, { useState, useRef, ChangeEvent } from 'react';
import { useAuth } from '../../context/AuthContext';
import { uploadProfilePicture, deleteProfilePicture } from '../../services/userProfileService';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { UserAvatar } from '../../components/ui/user-avatar';
import { Upload, Trash2, Image } from 'lucide-react';

/**
 * Component for uploading and managing a user's profile picture
 */
export const ProfilePictureUpload: React.FC = () => {
  const { user, profilePictureUrl, refreshProfilePicture } = useAuth();
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Handle file selection
  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    setError(null);
    const file = e.target.files?.[0];
    
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be less than 5MB');
      return;
    }
    
    // Create preview
    const reader = new FileReader();
    reader.onload = () => {
      setPreviewUrl(reader.result as string);
    };
    reader.readAsDataURL(file);
  };
  
  // Handle upload
  const handleUpload = async () => {
    if (!user || !fileInputRef.current?.files?.[0]) return;
    
    try {
      setIsUploading(true);
      setError(null);
      
      const file = fileInputRef.current.files[0];
      await uploadProfilePicture(user, file);
      
      // Refresh profile picture in context
      await refreshProfilePicture();
      
      // Clear preview and file input
      setPreviewUrl(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err: any) {
      console.error('Error uploading profile picture:', err);
      // Extract error message if available
      const errorMessage = err.message || 'Failed to upload image. Please try again.';
      setError(errorMessage.includes('Environment variables may be missing') 
        ? 'Server configuration error. Please contact an administrator.' 
        : errorMessage);
    } finally {
      setIsUploading(false);
    }
  };
  
  // Handle delete
  const handleDelete = async () => {
    if (!user || !user.uid) return;
    
    try {
      setIsUploading(true);
      setError(null);
      
      await deleteProfilePicture(user.uid);
      
      // Refresh profile picture in context
      await refreshProfilePicture();
      
      // Clear preview and file input
      setPreviewUrl(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err: any) {
      console.error('Error deleting profile picture:', err);
      // Extract error message if available
      const errorMessage = err.message || 'Failed to delete image. Please try again.';
      setError(errorMessage.includes('Environment variables may be missing') 
        ? 'Server configuration error. Please contact an administrator.' 
        : errorMessage);
    } finally {
      setIsUploading(false);
    }
  };
  
  // Trigger file input click
  const handleSelectFile = () => {
    fileInputRef.current?.click();
  };
  
  return (
    <Card className="dark:bg-gray-800 dark:border-gray-700">
      <CardHeader>
        <CardTitle className="text-gray-900 dark:text-white">Profile Picture</CardTitle>
        <CardDescription className="dark:text-gray-400">
          Upload a profile picture to personalize your account
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col sm:flex-row items-center gap-6">
          {/* Current profile picture or preview */}
          <div className="flex flex-col items-center gap-2">
            <div className="relative">
              {previewUrl ? (
                <div className="h-24 w-24 rounded-full overflow-hidden">
                  <img 
                    src={previewUrl} 
                    alt="Preview" 
                    className="h-full w-full object-cover"
                  />
                </div>
              ) : (
                <UserAvatar size="lg" className="h-24 w-24" />
              )}
            </div>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {previewUrl ? 'Preview' : 'Current'}
            </span>
          </div>
          
          {/* Upload controls */}
          <div className="flex flex-col gap-4 flex-1">
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleSelectFile}
                disabled={isUploading}
                className="flex items-center gap-2"
              >
                <Image className="h-4 w-4" />
                Select Image
              </Button>
              
              {previewUrl && (
                <Button
                  type="button"
                  variant="default"
                  size="sm"
                  onClick={handleUpload}
                  disabled={isUploading}
                  className="flex items-center gap-2"
                >
                  <Upload className="h-4 w-4" />
                  {isUploading ? 'Uploading...' : 'Upload'}
                </Button>
              )}
              
              {profilePictureUrl && !previewUrl && (
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  onClick={handleDelete}
                  disabled={isUploading}
                  className="flex items-center gap-2"
                >
                  <Trash2 className="h-4 w-4" />
                  Remove
                </Button>
              )}
            </div>
            
            {/* Hidden file input */}
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              accept="image/*"
              className="hidden"
            />
            
            {/* Error message */}
            {error && (
              <p className="text-sm text-red-500 dark:text-red-400">{error}</p>
            )}
            
            {/* Help text */}
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Recommended: Square image, 250x250 pixels or larger.
              <br />
              Maximum file size: 5MB.
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ProfilePictureUpload;
